import { useMemo } from 'react';
import rawCompanyNames from '../constants/companyNames.json';

const companyNamesMap = rawCompanyNames as Record<string, string>;

/**
 * Converts an ALL-CAPS or raw string into clean Title Case (e.g. "HDFC BANK LTD" -> "HDFC Bank Ltd").
 */
export const toTitleCase = (str: string): string => {
  if (!str) return '';
  // Check if string is mainly uppercase
  const letters = str.replace(/[^A-Za-z]/g, '');
  const isAllUpper = letters.length > 0 && letters === letters.toUpperCase();
  if (!isAllUpper) return str;

  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (!word) return '';
      // Retain standard acronyms
      const upper = word.toUpperCase();
      if (['LTD', 'CORP', 'INC', 'CO', 'LLC', 'PLC'].includes(upper)) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      if (['BSE', 'NSE', 'SBI', 'TCS', 'HDFC', 'ICICI', 'L&T', 'IT', 'AI', 'EV'].includes(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .replace(/\bLtd\b/i, 'Ltd')
    .replace(/\bCorp\b/i, 'Corp')
    .replace(/\bInc\b/i, 'Inc');
};

/**
 * Clean up redundant suffixes for compact UI presentation.
 * E.g., "Apollo Hospitals Enterprise Limited" -> "Apollo Hospitals Enterprise Ltd"
 */
export const cleanCompanyName = (name: string): string => {
  if (!name) return '';
  const cleaned = name
    .replace(/\s+Limited\.?\b/gi, ' Ltd')
    .replace(/\s+Corporation\.?\b/gi, ' Corp')
    .replace(/\s+Company\.?\b/gi, ' Co')
    .replace(/\s+Incorporated\.?\b/gi, ' Inc')
    .trim();
  return toTitleCase(cleaned);
};

/**
 * Synchronously retrieves official company name for a given stock symbol or scrip code.
 */
export const getCompanyName = (symbol?: string | null): string => {
  if (!symbol) return '';
  const upper = symbol.trim().toUpperCase();
  const direct = companyNamesMap[upper];
  if (direct) return cleanCompanyName(direct);

  // Strip -EQ, -BE suffixes if present
  const base = upper.replace(/-[A-Z0-9]+$/, '');
  const baseMatch = companyNamesMap[base];
  if (baseMatch) return cleanCompanyName(baseMatch);

  return '';
};

/**
 * Universally retrieves or cleanly formats a display name for a stock symbol,
 * guaranteeing consistent Title-Case presentation across the application.
 */
export const formatDisplayName = (symbol?: string | null, rawName?: string | null): string => {
  if (!symbol && !rawName) return '—';
  const official = getCompanyName(symbol);
  if (official) return official;
  if (rawName && rawName.trim().length > 0) {
    return cleanCompanyName(rawName);
  }
  return symbol || '—';
};

/**
 * React hook to retrieve company name for a given stock symbol.
 */
export const useCompanyName = (symbol?: string | null): string => {
  return useMemo(() => getCompanyName(symbol), [symbol]);
};
